import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Checkbox, Input, Modal, Select, Space, Spin } from "antd";
import APIService from "../../Service/APIService";
import {
  SHIPPING_STATUSES,
  canUpdateTracking,
  createShippingRequestId,
  shippingNoteError,
} from "./shippingStatusAccess";

export default function UpdateTrackingModal({ orderData, user, onClose, onSaved }) {
  const [snapshot, setSnapshot] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [status, setStatus] = useState("Shipped");
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ambiguous, setAmbiguous] = useState(false);
  const request = useRef(null);
  const busy = useRef(false);
  const mounted = useRef(true);
  const allowed = canUpdateTracking(user, orderData);
  const path = `directorders/${orderData.ID}/shipping-status`;

  const load = async () => {
    if (!allowed) {
      setLoading(false);
      setError("Tracking updates require an eligible direct order and Order Manage permission.");
      return;
    }
    setLoading(true);
    try {
      const response = await new APIService().getDataFromBackend(path);
      if (!mounted.current) return;
      const current = response.data.data;
      setSnapshot(current);
      setTrackingNumber(current.trackingNumber ?? "");
      setStatus(current.status);
      if (!current.canChangeStatus)
        setError("This order is no longer eligible for shipping updates, or changes are currently disabled.");
    } catch (err) {
      if (mounted.current) {
        setSnapshot(null);
        setError(err.response?.data?.message || "Unable to load current shipping details. Please retry.");
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
    // Parent keys this modal by order ID.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData.ID]);

  const trimmedTracking = trackingNumber.trim();
  const trackingChanged = Boolean(snapshot) && trimmedTracking !== String(snapshot.trackingNumber ?? "").trim();
  const statusChanged = Boolean(snapshot) && status !== snapshot.status;
  const trackingInvalid = !trimmedTracking || trimmedTracking.length > 255;
  const noteInvalid = Boolean(shippingNoteError(note));
  const canSave =
    allowed && snapshot?.canChangeStatus && !loading && !trackingInvalid && !noteInvalid &&
    (ambiguous || trackingChanged || statusChanged);

  const save = async () => {
    if (busy.current || !canSave) return;
    if (!request.current) {
      let requestId;
      try {
        requestId = createShippingRequestId();
      } catch (err) {
        setError(err.message);
        return;
      }
      request.current = {
        status,
        trackingNumber: trackingChanged ? trimmedTracking : undefined,
        shippingNote: note.trim() || null,
        notifyUsers: notify,
        expectedStatus: snapshot.status,
        expectedShippingStatusRevision: snapshot.shippingStatusRevision,
        expectedTrackingNumber: String(snapshot.trackingNumber).trim(),
        requestId,
      };
    }
    busy.current = true;
    setSaving(true);
    setError("");
    let result;
    try {
      const response = await new APIService().updateDataToBackend(request.current, path);
      result = response.data.data;
    } catch (err) {
      if (!mounted.current) return;
      if (err.response?.status === 409) {
        request.current = null;
        setAmbiguous(false);
        setError(err.response?.data?.message || "Order changed. Review the current details before saving again.");
        await load();
      } else if (!err.response || err.response.status >= 500) {
        setAmbiguous(true);
        setError("The save could not be confirmed. Retry Save to safely check the same request.");
      } else {
        request.current = null;
        setAmbiguous(false);
        setError(err.response?.data?.message || "Unable to update tracking.");
      }
    } finally {
      busy.current = false;
      if (mounted.current) setSaving(false);
    }
    if (result && mounted.current) {
      request.current = null;
      onSaved(result);
    }
  };

  const fieldDisabled = saving || loading || ambiguous || !allowed || !snapshot?.canChangeStatus;

  return (
    <Modal
      open
      title="Update Tracking"
      onCancel={() => {
        if (!busy.current) onClose();
      }}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      width={560}
      footer={
        <Space>
          <Button disabled={saving} onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} disabled={!canSave} onClick={save}>
            Save
          </Button>
        </Space>
      }
    >
      <p>Order {snapshot?.poNumber || orderData.PONumber || `#${orderData.ID}`}</p>
      {error && <Alert role="alert" type="error" message={error} style={{ marginBottom: 16 }} />}
      {loading ? (
        <Spin aria-label="Loading shipping details" />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <label htmlFor="update-tracking-number">Tracking Number</label>
            <Input
              id="update-tracking-number"
              value={trackingNumber}
              maxLength={255}
              disabled={fieldDisabled}
              status={trackingInvalid ? "error" : undefined}
              onChange={(event) => setTrackingNumber(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="update-tracking-status">Shipping Status</label>
            <Select
              id="update-tracking-status"
              aria-label="Shipping Status"
              style={{ width: "100%" }}
              value={status}
              disabled={fieldDisabled}
              onChange={setStatus}
              options={SHIPPING_STATUSES.map((value) => ({ value, label: value }))}
            />
          </div>
          <div>
            <label htmlFor="update-tracking-note">Shipping Note</label>
            <Input.TextArea
              id="update-tracking-note"
              rows={3}
              value={note}
              disabled={fieldDisabled}
              onChange={(event) => setNote(event.target.value)}
            />
            {noteInvalid && <div role="alert">{shippingNoteError(note)}</div>}
          </div>
          <Checkbox checked={notify} disabled={fieldDisabled} onChange={(event) => setNotify(event.target.checked)}>
            Notify related users via email
          </Checkbox>
          {!snapshot && <Button onClick={() => { setError(""); load(); }}>Retry loading</Button>}
        </Space>
      )}
    </Modal>
  );
}
